import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faUser, faCalendar, faL } from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faInstagram, faLinkedin, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import emailjs from 'emailjs-com';
import { ClipLoader } from 'react-spinners'; // Install this with 'npm install react-spinners'
import { API_BASE_URL } from "../components/api";

const BlogPost = () => {
  const { id } = useParams();
  const form = useRef();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}blog-posts/${id}/`);
        if (!response.ok) {
          throw new Error("Failed to fetch blog");
        }
        const data = await response.json();
        setBlog(data);
      } catch (err) {
        console.error(err);
        setError("Blog post not found");
      } finally {
        setLoadingBlog(false);
      }
    };

    fetchBlog();
  }, [id]);

  if (loadingBlog) {
    return (
      <div className="flex items-center justify-center h-screen">
        <h1 className="text-2xl font-bold text-gray-800">Loading blog...</h1>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="flex items-center justify-center h-screen">
        <h1 className="text-2xl font-bold text-gray-800">Blog post not found</h1>
      </div>
    );
  }

    //email sending 

    const sendEmail = (e) => {
      setLoading(true);
      e.preventDefault();
      emailjs.sendForm('service_bt2q0z7', 'template_kwl0ojh', form.current, 'Yz16g8qPG_-f9ArRT')
        .then((result) => {
            console.log(result.text);
            setLoading(false);
            window.alert('Email sent successfully!');
        }, (error) => {
            console.log(error.text);
            window.alert('Failed to send email. Please try again later.');
        });
      };

  const title = blog.title;
  const author = blog.fullname;
  const date = blog.created_at
    ? new Date(blog.created_at).toLocaleDateString()
    : "";
  const wordCount = blog.content ? blog.content.split(/\s+/).length : 0;
  const readingTime = `${Math.max(1, Math.round(wordCount / 200))} min read`;
  const image = blog.image;
  const contentHtml = blog.content;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <article className="bg-white shadow-lg rounded-lg overflow-hidden">
        <img 
          src={image} 
          alt={title} 
          className="w-full md:h-[350px] object-cover object-center"
        />
        <div className="p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
          <div className="flex items-center text-sm text-gray-600 mb-6">
            <span className="mr-3">
              <FontAwesomeIcon icon={faUser} className="mr-1" />
              {author}
            </span>
            <span className="mr-3">
              <FontAwesomeIcon icon={faCalendar} className="mr-1" />
              {date}
            </span>
            <span>
              <FontAwesomeIcon icon={faClock} className="mr-1" />
              {readingTime}
            </span>
          </div>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: contentHtml }} 
          />
        </div>
      </article>
      
      <div className="mt-8 bg-gray-100 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Connect with {author}</h2>
        <p className="text-gray-600 text-sm mb-4">{blog.email}</p>
        {(blog.twitter || blog.facebook || blog.instagram || blog.linkedin || blog.website) && (
          <div className="flex flex-wrap gap-3 mt-4">
            {blog.twitter && (
              <a 
                href={blog.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
              >
                <FontAwesomeIcon icon={faTwitter} className="mr-2" />
                Twitter/X
              </a>
            )}
            {blog.facebook && (
              <a 
                href={blog.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faFacebook} className="mr-2" />
                Facebook
              </a>
            )}
            {blog.instagram && (
              <a 
                href={blog.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white rounded-lg hover:from-purple-600 hover:via-pink-600 hover:to-red-600 transition-colors"
              >
                <FontAwesomeIcon icon={faInstagram} className="mr-2" />
                Instagram
              </a>
            )}
            {blog.linkedin && (
              <a 
                href={blog.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
              >
                <FontAwesomeIcon icon={faLinkedin} className="mr-2" />
                LinkedIn
              </a>
            )}
            {blog.website && (
              <a 
                href={blog.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                Website
              </a>
            )}
          </div>
        )}
      </div>
      <div className='bg-white rounded-lg shadow-md p-8'>
        <h2 className="mb-8 text-3xl font-bold text-center">Give us your <span className='text-[#59844F]'>Feedback</span></h2>
        <div className='flex justify-center'>
          <form ref={form} onSubmit={sendEmail} className="space-y-6 w-full max-w-[500px]">
            <div>
              <label htmlFor="name" className="block mb-2 text-lg font-medium text-gray-700">Name:</label>
              <input type="text" id="name" name="user_name" placeholder="Your name" className="w-full px-4 py-3 border rounded-md bg-[#f5f5f7] focus:outline-none focus:ring-2 focus:ring-[#0A8754]" required />
            </div>
            <div>
              <label htmlFor="email" className="block mb-2 text-lg font-medium text-gray-700">Email:</label>
              <input type="email" id="email" name="user_email" placeholder="Your email" className="w-full px-4 py-3 border rounded-md bg-[#f5f5f7] focus:outline-none focus:ring-2 focus:ring-[#0A8754]" required />
            </div>
            <div>
              <label htmlFor="message" className="block mb-2 text-lg font-medium text-gray-700">Message:</label>
              <textarea id="message" name="message" placeholder="Your message" className="w-full px-4 py-3 border rounded-md bg-[#f5f5f7] focus:outline-none focus:ring-2 focus:ring-[#0A8754]" rows="4" required></textarea>
            </div>
            <button type="submit" className="w-full bg-[#59844F] text-white px-8 py-3 rounded-md font-bold transition-colors duration-300 hover:bg-[#086A43]">Send Message</button>
          </form>
        </div>
        {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-green bg-opacity-75 z-50">
          <ClipLoader size={150} color={"#59844F"} />
        </div>
      )}
      </div>
    </div>
  );
};

export default BlogPost;